import React, { useCallback, useMemo, memo } from 'react';
import {
    CheckCircleIcon,
    CubeIcon,
    ForwardIcon,
    XMarkIcon,
} from '../../../automationStudioIcons';
import { EXECUTION_STATUS } from '../../../../AutomationStudio/hooks/useReplayExecution';
import { getEffectiveStepStatus, getAdjustedReplayStatus, isEvaluationStepTool } from '../../../../AutomationStudio/utils/stepExecutionDisplay';
import { getRecordingSkipReason } from '../../../../AutomationStudio/utils/replayEligibility';
import AutomationText from '../../shared/AutomationText';
import { convertIdsToDisplay, replaceFileRefsWithNames } from '../../../../../utils/automation/variableUtils';
import styles from '../../../../../styles/automationStudio/automationStudio.module.scss';

function StepRowInner({
    step,
    stepNumber,
    isSelected,
    isMultiSelected = false,
    onStepActivate,
    onStepSelect,
    replayStatus,
    replayStepDetail = null,
    executionConfidence = null,
    isLoadedFromTestcase = false,
    hasExecutedInSession = false,
    isReplayFlow = false,
    isRecordingSkipStep = false,
    isSavedPlanIdle = false,
    isLiveRunActive = false,
    allInstructions = [],
    variablesList = [],
    variablesListDescriptions = {},
    fileSuggestions = [],
    onContextMenu,
    hasValidationError = false,
    isDirty = false,
}) {
    const handleStepClick = useCallback((e) => {
        e.stopPropagation();
        onStepSelect?.(step.id, e);
        onStepActivate(step, e);
    }, [step, onStepActivate, onStepSelect]);

    const getConfidenceColor = (confidence) => {
        if (confidence >= 85) return 'high';
        if (confidence >= 50) return 'medium';
        return 'low';
    };

    const effectiveConfidence = executionConfidence ?? step.confidence ?? 80;
    const confidenceLevel = getConfidenceColor(effectiveConfidence);
    const isFunctionCall = step.stepType === 'function_call';
    const isEvaluationStep = isEvaluationStepTool(step);
    const isAIGeneratedStep = isEvaluationStepTool(step) && !step?.useAssertionCode;
    const isCustomStep = step.customStep === true;

    const stepStatusOptions = { isReplayFlow, isSavedPlanIdle, replayStatus };
    const status = getEffectiveStepStatus(step, replayStepDetail, allInstructions, stepStatusOptions);
    const safeReplayStatus = getAdjustedReplayStatus(step, replayStatus, replayStepDetail, allInstructions, stepStatusOptions);

    const isRecordingSkipVisual = isRecordingSkipStep;
    const terminalReplayStatuses = [
        EXECUTION_STATUS.PASSED,
        EXECUTION_STATUS.FAILED,
        EXECUTION_STATUS.SKIPPED,
        EXECUTION_STATUS.IN_PROGRESS,
    ];
    const isCustomPendingAfterSession =
        step.customStep === true &&
        step.status === 'pending' &&
        hasExecutedInSession &&
        !isReplayFlow;
    const isReplayQueued = (isReplayFlow || isCustomPendingAfterSession) && (
        !safeReplayStatus ||
        safeReplayStatus === EXECUTION_STATUS.NEW ||
        safeReplayStatus === EXECUTION_STATUS.PENDING
    );
    const showPendingReplayBorder =
        isCustomPendingAfterSession && safeReplayStatus === EXECUTION_STATUS.PENDING;
    const replayBorderClass = !isRecordingSkipVisual && safeReplayStatus && (
        terminalReplayStatuses.includes(safeReplayStatus) || showPendingReplayBorder
    )
        ? `${styles.replayIndicator} ${styles[safeReplayStatus.toLowerCase().replace(/-/g, '')]}`
        : '';
    const hasReplayStatus = safeReplayStatus && (
        terminalReplayStatuses.includes(safeReplayStatus) || showPendingReplayBorder
    );
    const hasExecutionDataGenerate = !hasReplayStatus && (
        status === 'success' ||
        status === 'failed' ||
        status === 'completed' ||
        status === 'failed_by_evaluation' ||
        typeof step.confidence === 'number' ||
        !!step.screenshot ||
        !!step.executionGroup?.mainAction?.details?.screenshotPath
    );
    const showDetails = isReplayFlow
        ? [EXECUTION_STATUS.PASSED, EXECUTION_STATUS.FAILED, EXECUTION_STATUS.SKIPPED].includes(safeReplayStatus)
        : (hasExecutionDataGenerate || !!step.tool || (step.customStep && !!(step.description || step.extractedData?.stepDescription)));
    const isPlanOnlyPending = (isLoadedFromTestcase && !hasExecutedInSession && (!safeReplayStatus || safeReplayStatus === EXECUTION_STATUS.NEW || safeReplayStatus === EXECUTION_STATUS.PENDING))
        || (isSavedPlanIdle && !isRecordingSkipVisual);
    const hasStatusBorder =
        status === 'running' ||
        status === 'success' ||
        status === 'completed' ||
        status === 'failed_by_evaluation' ||
        status === 'failed';
    const statusBorderClass = !replayBorderClass && !isPlanOnlyPending && !isRecordingSkipVisual && hasStatusBorder
        ? styles[`stepStatus-${status}`]
        : '';

    const rawStepTitle =
        step.description ||
        step.extractedData?.stepDescription ||
        step.extractedData?.description ||
        step.tool ||
        '';

    const stepDisplayTitle = useMemo(() => {
        const withVars = convertIdsToDisplay(
            rawStepTitle,
            variablesList,
            variablesListDescriptions
        );
        return replaceFileRefsWithNames(withVars, fileSuggestions) || rawStepTitle;
    }, [rawStepTitle, variablesList, variablesListDescriptions, fileSuggestions]);

    const getStatusIcon = () => {
        if (isRecordingSkipVisual) {
            return <ForwardIcon className={styles.stepStatusIcon} style={{ color: '#6B7280' }} />;
        }
        if (isSavedPlanIdle) return null;

        if (safeReplayStatus && safeReplayStatus !== EXECUTION_STATUS.NEW) {
            if (safeReplayStatus === EXECUTION_STATUS.PENDING) return null;
            if (safeReplayStatus === EXECUTION_STATUS.IN_PROGRESS) return (
                <span className={`${styles.asSpinner} ${styles.asSpinnerSm}`} style={{ borderTopColor: '#2563EB' }} aria-hidden role="presentation" />
            );
            if (safeReplayStatus === EXECUTION_STATUS.PASSED) return <CheckCircleIcon className={styles.stepStatusIcon} style={{ color: '#16A34A' }} />;
            if (safeReplayStatus === EXECUTION_STATUS.FAILED) return <XMarkIcon className={styles.stepStatusIcon} style={{ color: '#DC2626' }} />;
            if (safeReplayStatus === EXECUTION_STATUS.SKIPPED) return <ForwardIcon className={styles.stepStatusIcon} style={{ color: '#6B7280' }} />;
        }
        if (isPlanOnlyPending) return null;
        switch (status) {
            case 'running':
                return (
                    <span className={`${styles.asSpinner} ${styles.asSpinnerSm}`} style={{ borderTopColor: '#2563EB' }} aria-hidden role="presentation" />
                );
            case 'success':
            case 'completed':
            case 'failed_by_evaluation':
                return <CheckCircleIcon className={styles.stepStatusIcon} style={{ color: '#16A34A' }} />;
            case 'failed':
                return <XMarkIcon className={styles.stepStatusIcon} style={{ color: '#DC2626' }} />;
            default:
                return null;
        }
    };

    const isEffectivelySelected = isSelected || isMultiSelected;
    const recordingSkipTooltip = isRecordingSkipVisual
        ? getRecordingSkipReason(step.status)
        : undefined;
    const isAiVerified = !replayBorderClass &&
        (status === 'success' || status === 'completed' || status === 'failed_by_evaluation') &&
        !isPlanOnlyPending &&
        !isRecordingSkipVisual &&
        !isReplayQueued;

    return (
        <div
            className={`${styles.stepItem} ${isEffectivelySelected ? styles.selected : ''} ${isFunctionCall ? styles.functionCall :
                isAiVerified ? styles.aiVerified : ''
                } ${replayBorderClass} ${statusBorderClass} ${hasValidationError ? styles.stepItemValidationError : ''} ${isDirty ? styles.stepItemDirty : ''} ${isRecordingSkipVisual ? styles.recordingSkipStep : ''} ${recordingSkipTooltip ? styles.tooltipHost : ''}`}
            title={recordingSkipTooltip}
            aria-invalid={hasValidationError ? 'true' : undefined}
            data-step-validation-error={hasValidationError ? 'true' : 'false'}
            data-replay-status={safeReplayStatus || ''}
            data-step-verified={isAiVerified ? 'true' : 'false'}
            onClick={handleStepClick}
            onContextMenu={onContextMenu ? onContextMenu(step.id) : undefined}
            role="option"
            aria-selected={isMultiSelected}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStepClick(e);
                }
            }}
        >
            <div className={styles.stepItemMain}>
                <div className={styles.stepNumber}>{stepNumber}</div>
                <div className={styles.stepContent}>
                    <AutomationText
                        as="span"
                        text={stepDisplayTitle}
                        className={`${styles.tooltipHost} ${styles.stepDescription} ${styles.asTypoBody2}`}
                        title={stepDisplayTitle}
                    />
                    {(showDetails || isPlanOnlyPending) && (
                        <div className={styles.stepMeta}>
                            <span className={`${styles.stepTool} ${styles.asTypoCaption}`}>
                                {step.tool}
                            </span>
                            {!isReplayFlow && !isPlanOnlyPending && isEvaluationStep && effectiveConfidence != null && (status === 'success' || status === 'completed') && (
                                <div className={`${styles.aiConfidence} ${styles[confidenceLevel]}`}>
                                    <span className={styles.asTypoCaption} style={{ fontWeight: 500 }}>
                                        {effectiveConfidence}%
                                    </span>
                                </div>
                            )}
                            {isFunctionCall && (
                                <span className={`${styles.functionBadge} ${styles.asChip}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <CubeIcon className={styles.chipIcon} />
                                    Function
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.stepItemActions}>
                {isAIGeneratedStep && (
                    <span className={styles.aiLabel}>AI</span>
                )}
                {isCustomStep && (
                    <span className={styles.aiLabel}>Custom</span>
                )}
                {getStatusIcon()}
            </div>
        </div>
    );
}

function stepRowPropsAreEqual(prev, next) {
    if (prev.stepNumber !== next.stepNumber) return false;
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.isMultiSelected !== next.isMultiSelected) return false;
    if (prev.replayStatus !== next.replayStatus) return false;
    if (prev.executionConfidence !== next.executionConfidence) return false;
    if (prev.replayStepDetail !== next.replayStepDetail) return false;
    if (prev.isLoadedFromTestcase !== next.isLoadedFromTestcase) return false;
    if (prev.hasExecutedInSession !== next.hasExecutedInSession) return false;
    if (prev.isReplayFlow !== next.isReplayFlow) return false;
    if (prev.isRecordingSkipStep !== next.isRecordingSkipStep) return false;
    if (prev.isSavedPlanIdle !== next.isSavedPlanIdle) return false;
    if (prev.isLiveRunActive !== next.isLiveRunActive) return false;
    if (prev.allInstructions !== next.allInstructions) return false;
    if (prev.onStepActivate !== next.onStepActivate) return false;
    if (prev.onStepSelect !== next.onStepSelect) return false;
    if (prev.variablesList !== next.variablesList) return false;
    if (prev.variablesListDescriptions !== next.variablesListDescriptions) return false;
    if (prev.fileSuggestions !== next.fileSuggestions) return false;
    if (prev.hasValidationError !== next.hasValidationError) return false;
    if (prev.isDirty !== next.isDirty) return false;
    if (prev.onContextMenu !== next.onContextMenu) return false;

    const a = prev.step;
    const b = next.step;
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        a.id === b.id &&
        a.status === b.status &&
        a.passed === b.passed &&
        a.details?.passed === b.details?.passed &&
        a.description === b.description &&
        a.extractedData?.stepDescription === b.extractedData?.stepDescription &&
        a.extractedData?.description === b.extractedData?.description &&
        a.tool === b.tool &&
        a.confidence === b.confidence &&
        a.customStep === b.customStep &&
        a.stepType === b.stepType &&
        a.useAssertionCode === b.useAssertionCode &&
        a.screenshot === b.screenshot &&
        a.executionGroup?.mainAction?.details?.screenshotPath ===
        b.executionGroup?.mainAction?.details?.screenshotPath
    );
}

const StepRow = memo(StepRowInner, stepRowPropsAreEqual);

export default StepRow;
